// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
// @ts-nocheck
import {
  SimpleGrid,
  Paper,
  Text,
  Group,
  Stack,
  ThemeIcon,
  Card,
  Title,
  Button,
  List,
  Badge,
  Alert,
  Divider,
  Progress,
  Tooltip,
} from '@mantine/core';
import {
  IconUsers,
  IconCash,
  IconClock,
  IconMailForward,
  IconArrowUpRight,
  IconArrowDownRight,
  IconCalendar,
  IconPlayerPlay,
  IconPlus,
  IconAlertTriangle,
  IconCheck,
  IconArrowRight,
  IconShieldCheck,
  IconRefresh,
  IconScan,
  IconFileInvoice,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { formatCurrency } from '../../utils/format';

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  change?: number;
  color: string;
}

function StatCard({ title, value, icon, change, color }: StatCardProps) {
  const isPositive = change && change > 0;
  return (
    <Paper shadow="xs" p="md" radius="md" withBorder>
      <Group justify="space-between">
        <div>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            {title}
          </Text>
          <Text size="xl" fw={700} mt={4}>
            {value}
          </Text>
          {change !== undefined && (
            <Group gap={4} mt={4}>
              {isPositive ? (
                <IconArrowUpRight size={16} color="var(--mantine-color-green-6)" />
              ) : (
                <IconArrowDownRight size={16} color="var(--mantine-color-red-6)" />
              )}
              <Text size="xs" c={isPositive ? 'green' : 'red'} fw={500}>
                {Math.abs(change)}% from last period
              </Text>
            </Group>
          )}
        </div>
        <ThemeIcon size={48} radius="md" variant="light" color={color}>
          {icon}
        </ThemeIcon>
      </Group>
    </Paper>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Welcome back. Here is an overview of your workforce."
      />

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} mb="lg">
        <StatCard
          title="Total Employees"
          value="142"
          icon={<IconUsers size={24} />}
          change={3.2}
          color="blue"
        />
        <StatCard
          title="Pending Payroll"
          value={formatCurrency(287450.0)}
          icon={<IconCash size={24} />}
          color="green"
        />
        <StatCard
          title="Active Punches"
          value="98"
          icon={<IconClock size={24} />}
          change={-2.1}
          color="cyan"
        />
        <StatCard
          title="Pending Requests"
          value="12"
          icon={<IconMailForward size={24} />}
          change={15}
          color="orange"
        />
      </SimpleGrid>

      {/* ── Attention Required ─────────────────────────────────────────
           Unlike TimeTrex where users hunt through 198 menu items to find
           problems, our system surfaces issues automatically. Users see
           what needs action and resolve it in 1-2 clicks.
      */}
      <Card shadow="xs" padding="lg" radius="md" withBorder mb="lg">
        <Group justify="space-between" mb="md">
          <Title order={4}>Attention Required</Title>
          <Badge color="orange" variant="light" size="lg">3 items</Badge>
        </Group>
        <Stack gap="sm">
          <Alert
            icon={<IconAlertTriangle size={18} />}
            color="red"
            variant="light"
            title="5 Timesheet Exceptions"
          >
            <Group justify="space-between" ta="center">
              <Text size="sm">Missing punches and overtime alerts need review before payroll closes.</Text>
              <Button
                size="xs"
                variant="filled"
                color="red"
                icon={<IconArrowRight size={14} />}
                onClick={() => navigate('/admin/workforce/time/exceptions')}
              >
                Review Now
              </Button>
            </Group>
          </Alert>

          <Alert
            icon={<IconShieldCheck size={18} />}
            color="orange"
            variant="light"
            title="Payroll Ready for Approval"
          >
            <Group justify="space-between" ta="center">
              <Text size="sm">Jan 20 - Feb 2 payroll dry-run complete. No anomalies detected.</Text>
              <Button
                size="xs"
                variant="filled"
                color="orange"
                icon={<IconCheck size={14} />}
                onClick={() => navigate('/admin/workforce/payroll/run')}
              >
                Approve & Run
              </Button>
            </Group>
          </Alert>

          <Alert
            icon={<IconRefresh size={18} />}
            color="blue"
            variant="light"
            title="2 Leave Requests Pending"
          >
            <Group justify="space-between" ta="center">
              <Text size="sm">PTO requests from John Doe and Sarah Wilson await your approval.</Text>
              <Button
                size="xs"
                variant="filled"
                color="blue"
                icon={<IconArrowRight size={14} />}
                onClick={() => navigate('/admin/workforce/leave/requests')}
              >
                Review
              </Button>
            </Group>
          </Alert>
        </Stack>
      </Card>

      {/* ── Payroll Progress ────────────────────────────────────────────
           Shows where we are in the pay cycle. TimeTrex buries this info
           across multiple screens. We show it at a glance.
      */}
      <Card shadow="xs" padding="lg" radius="md" withBorder mb="lg">
        <Group justify="space-between" mb="sm">
          <Title order={4}>Current Pay Period</Title>
          <Badge color="green" variant="light">Jan 20 - Feb 2</Badge>
        </Group>
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Timesheets verified</Text>
            <Text size="sm" fw={500}>137 / 142</Text>
          </Group>
          <Progress value={96} color="teal" size="lg" radius="md" />
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Exceptions resolved</Text>
            <Text size="sm" fw={500}>8 / 13</Text>
          </Group>
          <Progress value={62} color="orange" size="lg" radius="md" />
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Payroll status</Text>
            <Badge color="yellow" variant="light" size="sm">Dry-run complete</Badge>
          </Group>
          <Divider my="xs" />
          <Tooltip label="Runs dry-run preview, detects anomalies, then asks for approval">
            <Button
              fullWidth
              variant="light"
              color="green"
              icon={<IconPlayerPlay size={18} />}
              onClick={() => navigate('/admin/workforce/payroll/run')}
            >
              Continue to Payroll Approval
            </Button>
          </Tooltip>
        </Stack>
      </Card>

      <SimpleGrid cols={{ base: 1, lg: 2 }} mb="lg">
        <Card shadow="xs" padding="lg" radius="md" withBorder>
          <Title order={4} mb="md">
            Quick Actions
          </Title>
          <SimpleGrid cols={2}>
            <Button
              variant="light"
              icon={<IconPlayerPlay size={18} />}
              onClick={() => navigate('/admin/workforce/payroll/run')}
            >
              Run Payroll
            </Button>
            <Button
              variant="light"
              icon={<IconPlus size={18} />}
              onClick={() => navigate('/admin/workforce/employees/new')}
            >
              Add Employee
            </Button>
            <Button
              variant="light"
              color="blue"
              icon={<IconScan size={18} />}
              onClick={() => navigate('/admin/workforce/onboarding/smart')}
            >
              Smart Onboard
            </Button>
            <Button
              variant="light"
              icon={<IconFileInvoice size={18} />}
              onClick={() => navigate('/admin/workforce/payroll/tax-filing')}
            >
              Tax Filing
            </Button>
            <Button
              variant="light"
              icon={<IconClock size={18} />}
              onClick={() => navigate('/admin/workforce/time')}
            >
              View Timesheets
            </Button>
            <Button
              variant="light"
              icon={<IconCalendar size={18} />}
              onClick={() => navigate('/admin/workforce/schedules')}
            >
              View Schedule
            </Button>
          </SimpleGrid>
        </Card>

        <Card shadow="xs" padding="lg" radius="md" withBorder>
          <Title order={4} mb="md">
            Upcoming Pay Periods
          </Title>
          <Stack gap="sm">
            <Paper p="sm" withBorder>
              <Group justify="space-between">
                <div>
                  <Text fw={500} size="sm">
                    Jan 20 - Feb 2, 2025
                  </Text>
                  <Text size="xs" c="dimmed">
                    Biweekly - Pay date: Feb 7
                  </Text>
                </div>
                <Badge color="yellow" variant="light">
                  Open
                </Badge>
              </Group>
            </Paper>
            <Paper p="sm" withBorder>
              <Group justify="space-between">
                <div>
                  <Text fw={500} size="sm">
                    Feb 3 - Feb 16, 2025
                  </Text>
                  <Text size="xs" c="dimmed">
                    Biweekly - Pay date: Feb 21
                  </Text>
                </div>
                <Badge color="gray" variant="light">
                  Upcoming
                </Badge>
              </Group>
            </Paper>
            <Paper p="sm" withBorder>
              <Group justify="space-between">
                <div>
                  <Text fw={500} size="sm">
                    Feb 17 - Mar 2, 2025
                  </Text>
                  <Text size="xs" c="dimmed">
                    Biweekly - Pay date: Mar 7
                  </Text>
                </div>
                <Badge color="gray" variant="light">
                  Upcoming
                </Badge>
              </Group>
            </Paper>
          </Stack>
        </Card>
      </SimpleGrid>

      <Card shadow="xs" padding="lg" radius="md" withBorder>
        <Title order={4} mb="md">
          Recent Activity
        </Title>
        <List spacing="sm" size="sm">
          <List.Item>
            <Group gap="xs">
              <Text size="sm">
                <Text span fw={500}>
                  Jane Smith
                </Text>{' '}
                approved payroll run for Jan 6-19 period
              </Text>
              <Text size="xs" c="dimmed">
                2 hours ago
              </Text>
            </Group>
          </List.Item>
          <List.Item>
            <Group gap="xs">
              <Text size="sm">
                <Text span fw={500}>
                  John Doe
                </Text>{' '}
                submitted time-off request (Jan 27-31)
              </Text>
              <Text size="xs" c="dimmed">
                4 hours ago
              </Text>
            </Group>
          </List.Item>
          <List.Item>
            <Group gap="xs">
              <Text size="sm">
                <Text span fw={500}>
                  Mike Johnson
                </Text>{' '}
                was added as a new employee
              </Text>
              <Text size="xs" c="dimmed">
                Yesterday
              </Text>
            </Group>
          </List.Item>
          <List.Item>
            <Group gap="xs">
              <Text size="sm">
                <Text span fw={500}>
                  Sarah Wilson
                </Text>{' '}
                resolved 3 timesheet exceptions
              </Text>
              <Text size="xs" c="dimmed">
                Yesterday
              </Text>
            </Group>
          </List.Item>
          <List.Item>
            <Group gap="xs">
              <Text size="sm">
                <Text span fw={500}>
                  System
                </Text>{' '}
                generated Q4 2024 tax summary report
              </Text>
              <Text size="xs" c="dimmed">
                2 days ago
              </Text>
            </Group>
          </List.Item>
        </List>
      </Card>
    </>
  );
}
